import { useState } from "react";
import { Popover } from "@base-ui/react/popover";
import { IconPlayerPlay } from "@tabler/icons-react";
import CountBadge from "../UI/CountBadge";
import Title from "../UI/Title";
import Text from "../UI/Text";
import Tooltip from "../UI/Tooltip";
import TutorialVideoList from "./TutorialVideoList";
import VideoTutorialPlayer, { type PlayerView } from "./VideoTutorialPlayer";
import useSeenTutorials from "./useSeenTutorials";
import {
  HELP_CENTER_URL,
  TUTORIALS,
  type TutorialPage,
  type TutorialVideo,
} from "./videoTutorials";
import styles from "./VideoTutorialsButton.module.css";

interface VideoTutorialsButtonProps {
  page: TutorialPage;
}

/**
 * Bouton « Tutos vidéo » d'un écran : liste les tutoriels de la page dans un
 * popover et ouvre le lecteur. Tant qu'il reste des vidéos jamais ouvertes,
 * le bouton affiche son libellé et le nombre de vidéos ; une fois tout vu,
 * il se réduit à une icône.
 */
export default function VideoTutorialsButton({
  page,
}: VideoTutorialsButtonProps) {
  const videos = TUTORIALS[page];
  const { seenIds, markSeen } = useSeenTutorials();
  const [menuOpen, setMenuOpen] = useState(false);
  const [current, setCurrent] = useState<TutorialVideo | null>(null);
  const [view, setView] = useState<PlayerView>("modal");
  // Le trigger compact est enveloppé dans un Tooltip (lui-même un Popover) :
  // le handle le rattache explicitement à notre popover et non à celui du Tooltip.
  const [popoverHandle] = useState(() => Popover.createHandle());

  const unseenCount = videos.filter((v) => !seenIds.includes(v.id)).length;
  const totalMinutes = Math.round(
    videos.reduce((sum, v) => sum + v.duration, 0) / 60,
  );

  function openVideo(video: TutorialVideo) {
    markSeen(video.id);
    setCurrent(video);
    setView("modal");
    setMenuOpen(false);
  }

  return (
    <>
      <Popover.Root
        handle={popoverHandle}
        open={menuOpen}
        onOpenChange={setMenuOpen}
      >
        {unseenCount > 0 ? (
          <Popover.Trigger
            handle={popoverHandle}
            render={
              <button type="button" className={styles.button}>
                <IconPlayerPlay size={18} className={styles.icon} />
                <Text as="span" variant="deci" weight="bold" color="primary">
                  Tutos vidéo
                </Text>
                <CountBadge count={videos.length} />
              </button>
            }
          />
        ) : (
          <Tooltip content="Tutos vidéo de cette page">
            <Popover.Trigger
              handle={popoverHandle}
              render={
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label="Tutos vidéo de cette page"
                >
                  <IconPlayerPlay size={18} />
                </button>
              }
            />
          </Tooltip>
        )}
        <Popover.Portal>
          <Popover.Positioner
            sideOffset={8}
            align="end"
            className={styles.positioner}
          >
            <Popover.Popup className={styles.popup}>
              <div className={styles.popupHeader}>
                <Title as="div" variant="deca" weight="bold">
                  Tutos de cette page
                </Title>
                <Text as="span" variant="deci">
                  {videos.length} vidéo{videos.length > 1 ? "s" : ""} ·{" "}
                  {totalMinutes} min
                </Text>
              </div>
              <TutorialVideoList
                videos={videos}
                seenIds={seenIds}
                currentId={current?.id}
                onSelect={openVideo}
              />
              <Text variant="deci" className={styles.popupFooter}>
                Retrouvez tous les tutos dans le{" "}
                <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
                  Centre d'aide
                </a>
                .
              </Text>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
      {current && (
        <VideoTutorialPlayer
          page={page}
          videos={videos}
          current={current}
          view={view}
          seenIds={seenIds}
          onViewChange={setView}
          onSelect={openVideo}
          onClose={() => setCurrent(null)}
        />
      )}
    </>
  );
}
